import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGUAGES } from "../../i18n";
import { Globe, Check } from "lucide-react";
import { track } from "../../lib/analytics";
import { stripLangPrefix, isPublicLocalizedPath } from "../../lib/langRoutes";
import {
  canonicalizeSeoSlug, seoSlugFor,
  canonicalizeBlogSlug, blogSlugFor,
  canonicalizeCapSlug, capSlugFor, capSegmentFor, CAP_SEGMENTS_ALL,
} from "../../lib/localizedSlugs";

/* Costruisce l'URL localizzata CANONICA per la lingua scelta: italiano alla
   root (senza /it/ e senza ?lang=), altre lingue /{lang}/{slug-localizzato}. */
function localizedTarget(code, pathname) {
  const base = stripLangPrefix(pathname) || "/";
  if (base === "/") return code === "it" ? "/" : `/${code}/`;
  const segs = base.split("/").filter(Boolean);
  if (segs[0] === "blog") {
    if (segs[1]) {
      const c = canonicalizeBlogSlug(segs[1]);
      if (c) return code === "it" ? `/blog/${blogSlugFor(c, "it")}` : `/${code}/blog/${blogSlugFor(c, code)}`;
    }
    return code === "it" ? "/blog" : `/${code}/blog`;
  }
  if ((CAP_SEGMENTS_ALL.includes(segs[0]) || segs[0] === "capabilities") && segs[1]) {
    const c = canonicalizeCapSlug(segs[1]);
    if (c) {
      return code === "it"
        ? `/${capSegmentFor("it")}/${capSlugFor(c, "it")}`
        : `/${code}/${capSegmentFor(code)}/${capSlugFor(c, code)}`;
    }
  }
  const seo = canonicalizeSeoSlug(segs[0]);
  if (seo) return code === "it" ? `/${seoSlugFor(seo, "it")}` : `/${code}/${seoSlugFor(seo, code)}`;
  return code === "it" ? base : `/${code}${base}`;
}

/**
 * Click-controlled language dropdown.
 * - Toggles open/closed via React state (a11y + keyboard + touch friendly).
 * - Closes on outside click and on Escape.
 * - Auto-positions: opens DOWN by default; flips UP only if there isn't
 *   enough room below the button.
 * - Persists choice via i18next (already wired to localStorage 'mtt_lang').
 */
const MENU_HEIGHT_PX = 260;  // ~6 items × ~40px + padding — used for placement decision

export default function LanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const current = LANGUAGES.find((l) => l.code === i18n.language?.slice(0, 2)) || LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Decide direction when opening, based on available space below the button.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    // Prefer DOWN; only flip UP if there isn't enough room AND the space above is bigger.
    setOpenUp(below < MENU_HEIGHT_PX && above > below);
  }, [open]);

  const pick = (code) => {
    track("lang_changed", { from: i18n.language?.slice(0, 2), to: code });
    i18n.changeLanguage(code);
    setOpen(false);
    // On public pages, navigate to the canonical localized URL
    // (it = root senza prefisso, altre lingue /{lang}/{slug-localizzato}).
    if (isPublicLocalizedPath(location.pathname)) {
      navigate(localizedTarget(code, location.pathname));
    }
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-slate-800 text-slate-200 border border-slate-700 hover:border-blue-500"
        data-testid="lang-switcher-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={14}/>
        <span>{current.flag}</span>
        {!compact && <span>{current.label}</span>}
      </button>
      {open && (
        <div
          className={`absolute right-0 w-44 rounded-md bg-slate-900 border border-slate-700 shadow-xl z-[60] overflow-hidden ${openUp ? "bottom-full mb-2" : "top-full mt-2"}`}
          role="listbox"
          data-testid="lang-switcher-menu"
        >
          {LANGUAGES.map((l) => {
            const active = i18n.language?.startsWith(l.code);
            return (
              <button
                key={l.code}
                onClick={() => pick(l.code)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-800 ${active ? "bg-blue-500/10 text-blue-300" : "text-slate-200"}`}
                data-testid={`lang-${l.code}`}
                role="option"
                aria-selected={active}
              >
                <span>{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                {active && <Check size={14} className="text-blue-400"/>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
